/**
 * Shadow settings: defaults, the migration of pre-overhaul scenes, and the device tier.
 *
 * Pure (no engine imports) so the editor and the tests share exactly what the runtime applies.
 *
 * Data model (all fields optional, so older scenes and older viewers keep working):
 * - Scene level, on `splatRelighting`: `shadowsEnabled` (master switch), `shadowIntensity`
 *   (how dark shadows are, 0-1), `splatsCastShadows`, `splatsReceiveShadows`,
 *   `shadowGroundPlane` + `shadowGroundY` / `shadowPlaneScale`, `shadowMobile`,
 *   `spotPointShadowsOnScan`, `shadowsVersion`.
 * - Light level, on `lights[]`: `castShadows`, `shadowSoftness` (0-1), `shadowMapSize`
 *   (resolution; a point light's is per cube face), `shadowBias`.
 *
 * Scenes saved before the overhaul have no `shadowsVersion`. In them `shadowsEnabled` made every
 * directional light cast (whatever its own `castShadows` said) onto a ground plane; that is what
 * `resolveSceneShadows` reproduces for them, on top of the scan now receiving shadows itself.
 *
 * Version 2 scenes: spot and point lights shadowed models only. Version 3 adds their shadows on
 * the scan (and the scan casting into them, and blocking their relit light), on unless
 * `spotPointShadowsOnScan` says false; older scenes resolve to false, and the editor's migration
 * writes false for a scene that already had a casting spot / point light, so it keeps its look
 * until the creator turns it on.
 */

/** Written by editors that know the per-light model. Absent = a pre-overhaul scene. */
export const SHADOWS_VERSION = 3;

/** The first version whose spot / point lights shadow the scan by default. */
export const SPOT_POINT_ON_SCAN_VERSION = 3;

/** Shadow map sizes offered in the editor. */
export const SHADOW_MAP_SIZES = [512, 1024, 2048, 4096] as const;

/** Directional lights that can shadow the splats themselves (the splat shader samples one map each). */
export const MAX_SPLAT_SHADOW_LIGHTS = 2;

/** Spot / point lights that can shadow the splats (the shader's slots; the tier may use fewer). */
export const MAX_SPLAT_LOCAL_SHADOW_LIGHTS = 4;

export type ShadowMobileMode = 'reduced' | 'off' | 'full';

export const SHADOW_DEFAULTS = {
    /**
     * How dark a full shadow is (0 = no darkening, 1 = black), as seen on screen: 0.7 leaves 30%
     * of the brightness, close to a real shadow baked into a sunny scan. New scenes.
     */
    strength: 0.7,
    /** What pre-overhaul viewers used when a scene had no `shadowIntensity`. */
    legacyStrength: 0.4,
    /** Edge softness 0 (crisp) - 1 (very soft). */
    softness: 0.35,
    resolution: 2048,
    /** World units; also the light's depth bias for models. */
    bias: 0.05,
    normalBias: 0.05,
    splatsCast: false,
    splatsReceive: true,
    groundPlane: false,
    groundY: 0,
    planeScale: 50,
    mobile: 'reduced' as ShadowMobileMode,
} as const;

/** Scene-level fields the resolver reads (a subset of SplatRelightingConfig). */
export interface SceneShadowFields {
    shadowsVersion?: number;
    shadowsEnabled?: boolean;
    shadowIntensity?: number;
    splatsCastShadows?: boolean;
    splatsReceiveShadows?: boolean;
    shadowGroundPlane?: boolean;
    shadowGroundY?: number;
    shadowPlaneScale?: number;
    shadowMobile?: ShadowMobileMode;
    spotPointShadowsOnScan?: boolean;
}

/** Light fields the resolver reads (a subset of LightConfig). */
export interface LightShadowFields {
    type?: string;
    enabled?: boolean;
    /** As authored (an animation can change the light's current intensity). */
    intensity?: number;
    castShadows?: boolean;
    shadowSoftness?: number;
    shadowMapSize?: number;
    shadowBias?: number;
    normalOffsetBias?: number;
}

export interface ResolvedLightShadow {
    cast: boolean;
    softness: number;
    resolution: number;
    bias: number;
    normalBias: number;
}

export interface ResolvedSceneShadows {
    /** Master switch, after migration. */
    enabled: boolean;
    /** Scene written by a pre-overhaul editor (no shadowsVersion). */
    legacy: boolean;
    strength: number;
    splatsCast: boolean;
    splatsReceive: boolean;
    /**
     * Spot and point lights shadow the scan, the scan casts into their maps (with `splatsCast`)
     * and their relit light stops at what blocks it. False: they shadow models only (scenes
     * saved before version 3).
     */
    spotPointOnScan: boolean;
    groundPlane: { enabled: boolean; y: number; scale: number };
    mobile: ShadowMobileMode;
    /** One entry per light, in `lights` order. */
    lights: ResolvedLightShadow[];
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Snap an authored size (shadowMapSize) to the nearest offered size; anything unusable → the default. */
export function normalizeShadowMapSize(size: unknown): number {
    if (!finite(size) || size <= 0) return SHADOW_DEFAULTS.resolution;
    let best: number = SHADOW_MAP_SIZES[0];
    for (const s of SHADOW_MAP_SIZES) if (Math.abs(Math.log2(s / size)) < Math.abs(Math.log2(best / size))) best = s;
    return best;
}

/** Whether a light type can cast shadows at all (hemispheric/ambient cannot). */
export function lightTypeCanCastShadows(type: string | undefined): boolean {
    return type === 'directional' || type === 'spot' || type === 'point';
}

/**
 * Resolve the scene's shadow settings, migrating a pre-overhaul scene.
 *
 * Legacy (no `shadowsVersion`): `shadowsEnabled` made every directional light cast, so a
 * directional light casts unless it explicitly says `castShadows: false`; spot / point lights
 * that already had `castShadows: true` keep casting (the old viewer honoured that flag even with
 * shadows off, so a legacy scene with such a light counts as shadows-on). The old ground plane
 * stays on at its stored height and size, now only where the scan has no surface.
 */
export function resolveSceneShadows(
    scene: SceneShadowFields | null | undefined,
    lights: ReadonlyArray<LightShadowFields | null | undefined>,
): ResolvedSceneShadows {
    const s = scene ?? {};
    const legacy = !finite(s.shadowsVersion);
    const legacyLightCasts = legacy && lights.some((l) => !!l && l.castShadows === true && lightTypeCanCastShadows(l.type));
    const enabled = s.shadowsEnabled === true || legacyLightCasts;
    const strength = clamp(finite(s.shadowIntensity) ? s.shadowIntensity : legacy ? SHADOW_DEFAULTS.legacyStrength : SHADOW_DEFAULTS.strength, 0, 1);
    const resolvedLights = lights.map((light): ResolvedLightShadow => {
        const l = light ?? {};
        let cast = false;
        if (enabled && l.enabled !== false && lightTypeCanCastShadows(l.type)) {
            if (legacy) {
                cast = l.type === 'directional'
                    ? (s.shadowsEnabled === true ? l.castShadows !== false : l.castShadows === true)
                    : l.castShadows === true;
            } else {
                cast = l.castShadows === true;
            }
        }
        return {
            cast,
            softness: clamp(finite(l.shadowSoftness) ? l.shadowSoftness : SHADOW_DEFAULTS.softness, 0, 1),
            resolution: normalizeShadowMapSize(l.shadowMapSize),
            bias: clamp(finite(l.shadowBias) ? l.shadowBias : SHADOW_DEFAULTS.bias, 0, 2),
            normalBias: clamp(finite(l.normalOffsetBias) ? l.normalOffsetBias : SHADOW_DEFAULTS.normalBias, 0, 2),
        };
    });
    return {
        enabled,
        legacy,
        strength,
        splatsCast: s.splatsCastShadows === true,
        splatsReceive: s.splatsReceiveShadows !== false,
        spotPointOnScan: typeof s.spotPointShadowsOnScan === 'boolean'
            ? s.spotPointShadowsOnScan
            : !legacy && (s.shadowsVersion as number) >= SPOT_POINT_ON_SCAN_VERSION,
        groundPlane: {
            enabled: enabled && (s.shadowGroundPlane ?? (legacy && s.shadowsEnabled === true)) === true,
            y: finite(s.shadowGroundY) ? s.shadowGroundY : SHADOW_DEFAULTS.groundY,
            scale: finite(s.shadowPlaneScale) && s.shadowPlaneScale > 0 ? s.shadowPlaneScale : SHADOW_DEFAULTS.planeScale,
        },
        mobile: s.shadowMobile === 'off' || s.shadowMobile === 'full' ? s.shadowMobile : SHADOW_DEFAULTS.mobile,
        lights: resolvedLights,
    };
}

/**
 * Rewrite an older scene into the current model (the editor does this on load, so the next save
 * is explicit). Returns the inputs unchanged when the scene is already current.
 * - Pre-overhaul (no version): per-light casting, as resolveSceneShadows reproduces it.
 * - Before version 3: `spotPointShadowsOnScan` false when a spot / point light already cast (its
 *   shadows stayed on models), so the scene keeps its look; true otherwise (nothing to keep, and
 *   a spot / point light added later gets the current behaviour).
 */
export function migrateLegacyShadowData<
    S extends SceneShadowFields,
    L extends LightShadowFields,
>(scene: S, lights: L[]): { scene: S; lights: L[]; migrated: boolean } {
    if (finite(scene.shadowsVersion) && scene.shadowsVersion >= SHADOWS_VERSION) return { scene, lights, migrated: false };
    const resolved = resolveSceneShadows(scene, lights);
    const legacy = resolved.legacy;
    const nextLights = legacy
        ? lights.map((light, i) => {
            const cast = resolved.lights[i].cast;
            if (!!light.castShadows === cast) return light;
            return { ...light, castShadows: cast };
        })
        : lights;
    const localCasts = lights.some((light, i) => resolved.lights[i].cast && (light.type === 'spot' || light.type === 'point'));
    const nextScene: S = {
        ...scene,
        shadowsVersion: SHADOWS_VERSION,
        ...(legacy && {
            shadowsEnabled: resolved.enabled,
            shadowIntensity: resolved.strength,
            shadowGroundPlane: resolved.groundPlane.enabled,
        }),
        spotPointShadowsOnScan: typeof scene.spotPointShadowsOnScan === 'boolean' ? scene.spotPointShadowsOnScan : !localCasts,
    };
    return { scene: nextScene, lights: nextLights, migrated: true };
}

export interface ShadowTierInput {
    /** Phone / tablet render profile. */
    mobile: boolean;
    /** The viewer's performance mode (also forced on for low-end phones). */
    performanceMode: boolean;
    /** The scene's choice for phones. */
    mobileMode?: ShadowMobileMode;
}

export interface ShadowTier {
    name: 'full' | 'reduced' | 'off';
    enabled: boolean;
    /** Shadow maps are capped at this size. */
    maxResolution: number;
    /** Splats may cast (an extra splat draw per shadow map update). */
    splatCasting: boolean;
    /** Extra shadow-map taps per splat on top of the centre tap (softness). */
    splatTaps: number;
    /** Spot / point lights whose shadows reach the splats (shadow lookups per splat each). */
    localSlots: number;
    /**
     * Spot / point lights the splats cast into (with splatCasting): a spot light draws the splats
     * once per update of its map, a point light six times.
     */
    localSplatCasters: number;
    /** Largest size of the shared spot / point shadow map (the engine's shadow atlas). */
    atlasMaxResolution: number;
}

/**
 * The shadow quality this device gets.
 * - Full (desktop): directional maps up to 4096; the scan casts (into directional maps and into
 *   the first FULL_TIER_LOCAL_SPLAT_CASTERS spot / point maps); the scan takes shadows from 2
 *   directional and 4 spot / point lights, 8 filter taps; spot / point maps share an atlas of up
 *   to 4096.
 * - Reduced (performance mode, or a phone whose scene keeps the default): 1024 directional maps,
 *   the scan doesn't cast, one tap per splat, the scan takes shadows from 2 directional lights
 *   and 1 spot / point light; the spot / point atlas stays at most 2048 (the engine's default,
 *   so models keep the shadows they had).
 * A scene can turn shadows off on phones, or give them the full tier.
 */
export function resolveShadowTier({ mobile, performanceMode, mobileMode = SHADOW_DEFAULTS.mobile }: ShadowTierInput): ShadowTier {
    if (mobile && mobileMode === 'off') {
        return { name: 'off', enabled: false, maxResolution: 0, splatCasting: false, splatTaps: 0, localSlots: 0, localSplatCasters: 0, atlasMaxResolution: 0 };
    }
    const reduced = performanceMode || (mobile && mobileMode !== 'full');
    if (reduced) {
        return { name: 'reduced', enabled: true, maxResolution: 1024, splatCasting: false, splatTaps: 0, localSlots: 1, localSplatCasters: 0, atlasMaxResolution: 2048 };
    }
    return {
        name: 'full', enabled: true, maxResolution: 4096, splatCasting: true, splatTaps: 8,
        localSlots: MAX_SPLAT_LOCAL_SHADOW_LIGHTS, localSplatCasters: FULL_TIER_LOCAL_SPLAT_CASTERS, atlasMaxResolution: 4096,
    };
}

/**
 * Spot / point lights the scan casts into on the full tier. A point light draws the scan into
 * six maps each time it moves, so this stays small (see docs/internal/FEATURES.md, Shadows).
 */
export const FULL_TIER_LOCAL_SPLAT_CASTERS = 2;

/**
 * Size of the shared spot / point shadow map (the engine's shadow atlas, split into a grid of
 * ceil(sqrt(n)) x ceil(sqrt(n)) tiles for n lights; a point light's tile holds its six faces in
 * 3 x 2). Each light asks for its resolution per map (per face for a point light); the atlas is
 * the largest ask times the grid, rounded up to a multiple of 256, between 1024 and the tier's
 * cap. Returns 0 with no spot / point light.
 */
export function atlasResolutionFor(lights: ReadonlyArray<{ type: 'spot' | 'point'; resolution: number }>, tier: Pick<ShadowTier, 'atlasMaxResolution'>): number {
    if (lights.length === 0 || tier.atlasMaxResolution <= 0) return 0;
    const grid = Math.ceil(Math.sqrt(lights.length));
    let tile = 0;
    for (const l of lights) tile = Math.max(tile, l.resolution * (l.type === 'point' ? 3 : 1));
    const wanted = Math.ceil((tile * grid) / 256) * 256;
    return clamp(wanted, Math.min(1024, tier.atlasMaxResolution), tier.atlasMaxResolution);
}

/** Kernel radius in shadow-map texels for a softness value (0-1). */
export function softnessToKernelTexels(softness: number): number {
    return 1 + clamp(softness, 0, 1) * 5;
}
